/**
 * A aritmética do mês mostrado na grade.
 *
 * Papel: aqui ficam `celulasDoMes`, que monta as semanas completas de um mês a
 * partir do primeiro dia da semana escolhido na barra, `pontasDaGrade`, o
 * recorte que a tela pede à API, `semanasDaCopia`, as duas semanas da cópia, e
 * os helpers de navegação e de rótulo que a página usa para andar de mês em mês
 * e para escrever o dia por extenso.
 *
 * Como usar: tudo aqui trabalha com data em `AAAA-MM-DD` e com `Date` em UTC.
 * Usar o fuso local faria o dia 1 de um mês virar o último do anterior para
 * quem está a oeste de Greenwich, e a grade inteira deslizaria uma casa.
 *
 * `PrimeiroDia` conta como o `Date.getDay`: 0 é domingo e 1 é segunda. Quem
 * monta a grade, a folha impressa e a cópia da semana deverá passar o mesmo
 * valor às três, ou a cópia levaria uma semana diferente da que a grade mostra.
 */

/** O dia em que a semana da grade começa: 0 é domingo, 1 é segunda. */
export type PrimeiroDia = 0 | 1;

/**
 * Os dias da semana por extenso, na contagem do `Date.getUTCDay`: o índice 0 é
 * domingo e o 6 é sábado.
 */
const NOMES_DOS_DIAS = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const;

/** Um dia da linha de cabeçalho da grade. */
export interface DiaDaSemanaNaGrade {
  /** O nome por extenso, que o CSS põe em caixa alta. */
  nome: string;
  /** Sábado ou domingo, que a linha de cabeçalho pinta de `brass`. */
  fimDeSemana: boolean;
}

/**
 * Os sete dias da linha de cabeçalho, na ordem em que a grade os desenha.
 *
 * @param primeiroDia - O dia em que a semana começa.
 * @returns Os sete dias, do primeiro ao último da semana.
 */
export function diasDaSemanaNaGrade(primeiroDia: PrimeiroDia): DiaDaSemanaNaGrade[] {
  return Array.from({ length: 7 }, (_, indice) => {
    const contagem = (primeiroDia + indice) % 7;
    return {
      nome: NOMES_DOS_DIAS[contagem] ?? '',
      fimDeSemana: contagem === 0 || contagem === 6,
    };
  });
}

/**
 * Os meses por extenso, na ordem do ano: `MESES[0]` é janeiro.
 *
 * O índice é o mês contado de 0, e `MesMostrado.mes` conta de 1 — quem indexa
 * por `mostrado.mes` sem subtrair um mostra o mês seguinte, e em dezembro
 * recebe `undefined`.
 */
export const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** O mês que a grade está mostrando. */
export interface MesMostrado {
  ano: number;
  /** O mês de 1 a 12, e não de 0 a 11 como o `Date` conta. */
  mes: number;
}

/** Uma célula da grade: um dia, dentro ou fora do mês mostrado. */
export interface CelulaDaGrade {
  /** A data em `AAAA-MM-DD`. */
  dia: string;
  /** O número do dia, que é o que aparece na célula. */
  numero: number;
  /** `false` nas células de completar a semana, que vêm do mês vizinho. */
  doMes: boolean;
}

/** A data em `AAAA-MM-DD` de um instante UTC. */
function emIso(data: Date): string {
  return data.toISOString().slice(0, 10);
}

/** O instante UTC da meia-noite de uma data em `AAAA-MM-DD`. */
function deIso(dia: string): Date {
  const [ano, mes, numero] = dia.split('-').map(Number);
  return new Date(Date.UTC(ano ?? 1970, (mes ?? 1) - 1, numero ?? 1));
}

/**
 * Quantos dias faltam do começo da semana até uma data.
 *
 * @param data - O instante em UTC.
 * @param primeiroDia - O dia em que a semana começa.
 * @returns De 0, quando a data já é o primeiro dia, a 6, quando é o último.
 */
function diasDesdeOInicio(data: Date, primeiroDia: PrimeiroDia): number {
  return (data.getUTCDay() - primeiroDia + 7) % 7;
}

/**
 * Monta as células da grade de um mês, em semanas inteiras.
 *
 * A grade sempre fecha a semana nas duas pontas, com dias do mês anterior e do
 * seguinte marcados com `doMes: false`. Sem isso, a primeira linha começaria no
 * meio e as colunas deixariam de corresponder aos dias da semana.
 *
 * @param mostrado - O ano e o mês, com o mês contado de 1 a 12.
 * @param primeiroDia - O dia em que cada semana começa.
 * @returns As células em ordem, do primeiro dia da primeira semana ao último
 *   da última. São sempre múltiplo de sete, de 28 a 42.
 */
export function celulasDoMes(
  mostrado: MesMostrado,
  primeiroDia: PrimeiroDia = 1,
): CelulaDaGrade[] {
  const primeiro = new Date(Date.UTC(mostrado.ano, mostrado.mes - 1, 1));
  const ultimo = new Date(Date.UTC(mostrado.ano, mostrado.mes, 0));

  const inicio = new Date(
    primeiro.getTime() - diasDesdeOInicio(primeiro, primeiroDia) * 86_400_000,
  );
  const fim = new Date(
    ultimo.getTime() + (6 - diasDesdeOInicio(ultimo, primeiroDia)) * 86_400_000,
  );

  const celulas: CelulaDaGrade[] = [];
  for (let instante = inicio.getTime(); instante <= fim.getTime(); instante += 86_400_000) {
    const data = new Date(instante);
    celulas.push({
      dia: emIso(data),
      numero: data.getUTCDate(),
      doMes: data.getUTCMonth() === mostrado.mes - 1,
    });
  }

  return celulas;
}

/**
 * As duas pontas do mês, do dia 1 ao último.
 *
 * É o recorte do que a coluna conta como "deste mês" — as folgas, os conflitos,
 * as diárias e as férias da lista — e o do "Limpar mês inteiro", que não apaga
 * os dias vizinhos que a grade mostra.
 *
 * @param mostrado - O ano e o mês, com o mês contado de 1 a 12.
 * @returns `de` no dia 1 e `ate` no último dia do mês.
 */
export function pontasDoMes(mostrado: MesMostrado): { de: string; ate: string } {
  return {
    de: emIso(new Date(Date.UTC(mostrado.ano, mostrado.mes - 1, 1))),
    ate: emIso(new Date(Date.UTC(mostrado.ano, mostrado.mes, 0))),
  };
}

/**
 * As duas pontas da grade, que é o recorte pedido à API.
 *
 * O recorte é a grade, e não o mês: a escala se monta por semana, e as células
 * dos meses vizinhos mostram o que está gravado nelas. Pedir só o mês deixaria a
 * semana da virada pela metade na tela.
 *
 * @param mostrado - O ano e o mês, com o mês contado de 1 a 12.
 * @param primeiroDia - O dia em que cada semana começa.
 * @returns `de` na primeira célula e `ate` na última.
 */
export function pontasDaGrade(
  mostrado: MesMostrado,
  primeiroDia: PrimeiroDia,
): { de: string; ate: string } {
  const celulas = celulasDoMes(mostrado, primeiroDia);
  return { de: celulas[0]?.dia ?? '', ate: celulas[celulas.length - 1]?.dia ?? '' };
}

/** Um intervalo fechado de datas em `AAAA-MM-DD`. */
export interface Intervalo {
  de: string;
  ate: string;
}

/**
 * As duas semanas da cópia: a que recebe e a imediatamente anterior a ela.
 *
 * A data escolhida cai dentro da semana de destino, e a origem é sempre a
 * semana de antes. Com a semana começando na segunda, escolher um domingo leva
 * a cópia para a semana que termina nele; com a semana começando no domingo, a
 * mesma data abre a semana seguinte.
 *
 * @param dia - Uma data qualquer da semana de destino, em `AAAA-MM-DD`.
 * @param primeiroDia - O dia em que cada semana começa.
 * @returns As semanas de `origem` e de `destino`, com sete dias cada.
 */
export function semanasDaCopia(
  dia: string,
  primeiroDia: PrimeiroDia,
): { origem: Intervalo; destino: Intervalo } {
  const data = deIso(dia);
  const inicio = data.getTime() - diasDesdeOInicio(data, primeiroDia) * 86_400_000;
  const semana = (deslocamento: number): Intervalo => ({
    de: emIso(new Date(inicio + deslocamento * 86_400_000)),
    ate: emIso(new Date(inicio + (deslocamento + 6) * 86_400_000)),
  });
  return { origem: semana(-7), destino: semana(0) };
}

/**
 * O dia e o mês de uma data, como a caixa da cópia os escreve.
 *
 * @param dia - A data em `AAAA-MM-DD`.
 * @returns A data em `DD/MM`.
 */
export function diaEMes(dia: string): string {
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
}

/**
 * O dia da semana de uma data, na contagem do `Date.getUTCDay`.
 *
 * É a chave da escala padrão, que é de cada dia da semana e não muda com o
 * primeiro dia escolhido na barra.
 *
 * @param dia - A data em `AAAA-MM-DD`.
 * @returns De 0, domingo, a 6, sábado.
 */
export function diaDaSemana(dia: string): number {
  return deIso(dia).getUTCDay();
}

/**
 * O mês vizinho, para os botões de navegação.
 *
 * @param mostrado - O mês de onde se parte.
 * @param passo - `-1` para o anterior, `1` para o seguinte.
 * @returns O mês de destino, com o ano já virado quando for o caso.
 */
export function mesVizinho(mostrado: MesMostrado, passo: number): MesMostrado {
  const data = new Date(Date.UTC(mostrado.ano, mostrado.mes - 1 + passo, 1));
  return { ano: data.getUTCFullYear(), mes: data.getUTCMonth() + 1 };
}

/**
 * O rótulo do mês, que é o nome acessível da grade.
 *
 * Na tela o mês se lê pela escolha do mês e pelo campo do ano, que são dois
 * controles separados; a grade leva este rótulo para quem a percorre por leitor
 * de tela saber de que mês são os dias.
 *
 * @param mostrado - O ano e o mês, com o mês contado de 1 a 12.
 * @returns O mês por extenso seguido do ano, como `Março de 2026`.
 */
export function rotuloDoMes(mostrado: MesMostrado): string {
  return `${MESES[mostrado.mes - 1] ?? ''} de ${String(mostrado.ano)}`;
}

/**
 * Um dia por extenso, com o dia da semana entre parênteses.
 *
 * É o texto do título da caixa do dia e de cada dia da lista de conflitos do
 * mês. O dia da semana sai de `Date` em UTC, pelo mesmo motivo do resto do
 * arquivo: no fuso local, a meia-noite de `AAAA-MM-DD` cairia no dia anterior a
 * oeste de Greenwich, e o título diria `(domingo)` numa segunda-feira.
 *
 * @param dia - A data em `AAAA-MM-DD`.
 * @returns O dia sem zero à esquerda, o mês e o dia da semana, como
 *   `7 de Setembro (segunda-feira)`.
 */
export function diaPorExtenso(dia: string): string {
  const data = deIso(dia);
  const semana = NOMES_DOS_DIAS[data.getUTCDay()] ?? '';
  return `${String(data.getUTCDate())} de ${MESES[data.getUTCMonth()] ?? ''} (${semana})`;
}
